import type {
    TableColumnsType,
    TableColumnType,
} from 'antd';
import {
    isValidElement,
    ReactNode,
} from 'react';

import { ColumnKey } from '@/constants';

export const MOBILE_TABLE_STACK_KEY = '__mobileStack';

const isStackColumn = <T,>(col: TableColumnType<T>): boolean =>
    col.key === MOBILE_TABLE_STACK_KEY;

type TableColumnDef<T> = TableColumnsType<T>[number];

const getByPath = (source: unknown, path: readonly (string | number)[]): unknown => {
    let current: unknown = source;

    for (const segment of path) {
        if (current === null || current === undefined) {
            return undefined;
        }

        if (typeof current !== 'object') {
            return undefined;
        }

        current = (current as Record<string | number, unknown>)[segment];
    }

    return current;
};

export const flattenLeafColumns = <T,>(cols: TableColumnsType<T>): TableColumnType<T>[] => {
    const out: TableColumnType<T>[] = [];
    for (const col of cols) {
        if ('children' in col && col.children && col.children.length > 0) {
            out.push(...flattenLeafColumns(col.children));
        } else {
            out.push(col);
        }
    }
    return out;
};

const normalizeTitle = <T,>(col: TableColumnType<T>): ReactNode => {
    const t = col.title;
    if (t === undefined || t === null || t === '') {
        return '';
    }
    if (typeof t === 'string' || typeof t === 'number') {
        return t;
    }
    if (isValidElement(t)) {
        return t;
    }
    return String(col.key ?? '');
};

const getCellValue = <T,>(record: T, col: TableColumnType<T>): unknown => {
    const idx = col.dataIndex;
    if (idx === undefined || idx === null) {
        return undefined;
    }
    if (Array.isArray(idx)) {
        return getByPath(record, idx as (string | number)[]);
    }
    return getByPath(record, [idx as string | number]);
};

const renderStackValue = <T,>(col: TableColumnType<T>, record: T, index: number): ReactNode => {
    const value = getCellValue(record, col);
    if (col.render) {
        return col.render(value, record, index) as ReactNode;
    }
    return value as ReactNode;
};

const MobileStackInner = <T,>({
    columns,
    record,
    index,
}: {
    columns: TableColumnType<T>[];
    record: T;
    index: number;
}) => (
    <div className="default-table-mobile-stack-inner">
        {columns.map((col, colIndex) => {
            if (isStackColumn(col) || col.hidden) {
                return null;
            }
            const rowKey = String(col.key ?? col.dataIndex ?? colIndex);
            const isActionsRow = col.key === ColumnKey.Actions;
            return (
                <div
                    key={rowKey}
                    className={
                        isActionsRow
                            ? 'default-table-mobile-stack-row default-table-mobile-stack-row--actions'
                            : 'default-table-mobile-stack-row'
                    }
                >
                    <div className="default-table-mobile-stack-label">{normalizeTitle(col)}</div>
                    <div className="default-table-mobile-stack-value">
                        {renderStackValue(col, record, index)}
                    </div>
                </div>
            );
        })}
    </div>
);

const mapColumnResponsiveLg = <T,>(col: TableColumnDef<T>): TableColumnDef<T> => {
    if ('children' in col && col.children && col.children.length > 0) {
        return {
            ...col,
            children: col.children.map(mapColumnResponsiveLg),
        };
    }
    if (col.responsive !== undefined) {
        return col;
    }
    return { ...col, responsive: ['lg'] };
};

export const withMobileTableColumns = <T,>(
    columns: TableColumnsType<T>,
    options?: { enable?: boolean },
): TableColumnsType<T> => {
    const enable = options?.enable !== false;
    if (!enable) {
        return columns;
    }

    const sourceLeaves = flattenLeafColumns(columns).filter(
        (c) => !isStackColumn(c) && !c.hidden,
    );

    const desktopCols = columns.map(mapColumnResponsiveLg);

    const mobileCol: TableColumnType<T> = {
        key: MOBILE_TABLE_STACK_KEY,
        className: 'default-table-mobile-stack-col',
        ellipsis: false,
        onCell: () => ({
            className: 'default-table-mobile-stack-cell',
        }),
        title: '',
        render: (_: unknown, record: T, index: number) => (
            <MobileStackInner columns={sourceLeaves} record={record} index={index} />
        ),
    };

    return [mobileCol, ...desktopCols];
};
